import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  Timestamp,
  getDoc,
} from "firebase/firestore";
import { getFirebaseDB } from "./firebase";
import { UserFavorite } from "@/types/types";

/**
 * Add a property to user's favorites
 */
export async function addFavorite(
  userId: string,
  propertyId: string
): Promise<void> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const favoriteRef = doc(db, `users/${userId}/favorites`, propertyId);

  await setDoc(favoriteRef, {
    propertyId,
    addedAt: Timestamp.now().toDate().toISOString(),
  });
}

/**
 * Remove a property from user's favorites
 */
export async function removeFavorite(
  userId: string,
  propertyId: string
): Promise<void> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const favoriteRef = doc(db, `users/${userId}/favorites`, propertyId);
  await deleteDoc(favoriteRef);
}

/**
 * Get all user's favorites (one-time fetch)
 */
export async function getFavorites(userId: string): Promise<string[]> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const favoritesRef = collection(db, `users/${userId}/favorites`);
  const snapshot = await getDocs(favoritesRef);

  return snapshot.docs.map((doc) => doc.id);
}

/**
 * Check if a property is favorited by user
 */
export async function isFavorite(
  userId: string,
  propertyId: string
): Promise<boolean> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const favoriteRef = doc(db, `users/${userId}/favorites`, propertyId);
  const snapshot = await getDoc(favoriteRef);

  return snapshot.exists();
}

/**
 * Subscribe to user's favorites (real-time)
 */
export function subscribeToFavorites(
  userId: string,
  callback: (favoriteIds: string[]) => void,
  errorCallback?: (error: Error) => void
): () => void {
  const db = getFirebaseDB();
  if (!db) {
    errorCallback?.(new Error("Firebase not initialized"));
    return () => {};
  }

  const favoritesRef = collection(db, `users/${userId}/favorites`);

  const unsubscribe = onSnapshot(
    favoritesRef,
    (snapshot) => {
      const favoriteIds = snapshot.docs.map((doc) => doc.id);
      callback(favoriteIds);
    },
    (error) => {
      console.error("Error subscribing to favorites:", error);
      errorCallback?.(error);
    }
  );

  return unsubscribe;
}

/**
 * Get favorites with full property data
 */
export async function getFavoritesWithDetails(
  userId: string
): Promise<UserFavorite[]> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const favoritesRef = collection(db, `users/${userId}/favorites`);
  const snapshot = await getDocs(favoritesRef);

  return snapshot.docs.map((doc) => ({
    propertyId: doc.id,
    addedAt: doc.data().addedAt,
  }));
}
